// src/pages/reports/ManualRequestsReport.js

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { fetchManualRequests, buildManualExcelUrl } from "../../services/webReportsService";
import AppHeader from "../../components/AppHeader";
import { formatYangon } from "../../services/datetimeService";

const STATUSES = ["", "draft", "submitted", "in_review", "approved", "rejected", "cancelled"];

const statusColor = {
  draft: "default",
  submitted: "info",
  in_review: "warning",
  approved: "success",
  rejected: "error",
  cancelled: "default",
};

const toDateKey = (date) => {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export default function ManualRequestsReport() {
  const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const now = new Date();
    const from = new Date(now);
    from.setDate(now.getDate() - 7);

    setDateFrom(toDateKey(from));
    setDateTo(toDateKey(now));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearch.trim());
    }, 350);

    return () => clearTimeout(timer);
  }, [employeeSearch]);

  const canSearch = useMemo(() => Boolean(dateFrom && dateTo), [dateFrom, dateTo]);

  const showMessage = useCallback((severity, message) => {
    setSnackbar({ open: true, severity, message });
  }, []);

  const loadReport = useCallback(async () => {
    if (!tenant) {
      showMessage("error", "Missing tenant configuration.");
      return;
    }
    if (!canSearch) return;

    setLoading(true);
    try {
      const res = await fetchManualRequests({
        date_from: dateFrom,
        date_to: dateTo,
        status,
        employee_search: debouncedEmployeeSearch || undefined,
      });

      setRows(Array.isArray(res?.rows) ? res.rows : (res?.data || []));
    } catch (error) {
      setRows([]);
      showMessage("error", error?.message || "Failed to load manual requests.");
    } finally {
      setLoading(false);
    }
  }, [canSearch, dateFrom, dateTo, debouncedEmployeeSearch, showMessage, status, tenant]);

  useEffect(() => {
    if (tenant && canSearch) {
      loadReport();
    }
  }, [tenant, canSearch, loadReport]);

  const onExportExcel = useCallback(async () => {
    if (!tenant) {
      showMessage("error", "Missing tenant configuration.");
      return;
    }

    setLoading(true);
    try {
      const url = buildManualExcelUrl({
        date_from: dateFrom,
        date_to: dateTo,
        status,
        employee_search: debouncedEmployeeSearch || undefined,
      });

      const res = await fetch(url, {
        credentials: "include",
        headers: { "X-Tenant": tenant },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Export failed: HTTP ${res.status}`);
      }

      const blob = await res.blob();
      const anchor = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      anchor.href = objectUrl;
      anchor.download = `manual_requests_${dateFrom}_${dateTo}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      showMessage("error", error?.message || "Export failed.");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, debouncedEmployeeSearch, showMessage, status, tenant]);

  const columnDefs = useMemo(() => [
    {
      headerName: "Request Date",
      field: "request_date",
      width: 130,
      valueFormatter: ({ value }) => String(value || "").slice(0, 10) || "-",
      headerClass: "premium-grid-header",
    },
    {
      headerName: "Employee Code",
      field: "employee_number",
      width: 150,
      valueGetter: ({ data }) => data?.employee_number || data?.staff_code || "-",
      headerClass: "premium-grid-header",
    },
    { headerName: "Staff Name", field: "full_name", minWidth: 190, flex: 1, headerClass: "premium-grid-header" },
    { headerName: "Department", field: "department", minWidth: 150, headerClass: "premium-grid-header" },
    {
      headerName: "Status",
      field: "status",
      width: 140,
      cellRenderer: ({ value }) => (
        <Chip
          size="small"
          label={value || "-"}
          color={statusColor[value] || "default"}
          variant="outlined"
          sx={{ fontWeight: 700, textTransform: "capitalize" }}
        />
      ),
      headerClass: "premium-grid-header",
    },
    { headerName: "Reason", field: "reason", minWidth: 220, flex: 1, headerClass: "premium-grid-header" },
    {
      headerName: "Req In",
      field: "requested_clock_in_time",
      width: 170,
      valueFormatter: ({ value }) => formatYangon(value) || "-",
      headerClass: "premium-grid-header",
    },
    {
      headerName: "Req Out",
      field: "requested_clock_out_time",
      width: 170,
      valueFormatter: ({ value }) => formatYangon(value) || "-",
      headerClass: "premium-grid-header",
    },
    {
      headerName: "Pending ClockOut",
      field: "has_pending_clockout",
      width: 150,
      cellStyle: { textAlign: "center" },
      valueFormatter: ({ value }) => (value === null || value === undefined ? "-" : String(value)),
      headerClass: "premium-grid-header",
    },
    {
      headerName: "Submitted At",
      field: "created_at",
      width: 170,
      valueFormatter: ({ value }) => formatYangon(value) || "-",
      headerClass: "premium-grid-header",
    },
    { headerName: "Reviewer", field: "reviewed_by", minWidth: 150, headerClass: "premium-grid-header" },
    { headerName: "Reviewer Comment", field: "reviewer_comment", minWidth: 220, flex: 1, headerClass: "premium-grid-header" },
    {
      headerName: "Reviewed At",
      field: "updated_at",
      width: 170,
      valueFormatter: ({ value }) => formatYangon(value) || "-",
      headerClass: "premium-grid-header",
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
  }), []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <AppHeader />

      <Fade in timeout={500}>
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} gap={2}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-0.02em", color: "#0f172a" }}>
                Manual Requests Report
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                Review manual attendance requests by date, status, employee code, or employee name.
              </Typography>
            </Box>

            <Stack direction="row" gap={1}>
              <Tooltip title="Refresh report">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadReport}
                    disabled={loading || !canSearch}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                  >
                    Refresh
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={onExportExcel}
                disabled={loading || rows.length === 0 || !canSearch}
                sx={{ bgcolor: "#6366f1", borderRadius: 2, textTransform: "none", fontWeight: 800, "&:hover": { bgcolor: "#4f46e5" } }}
              >
                Export Excel
              </Button>
            </Stack>
          </Stack>

          <Paper sx={{ p: 2, borderRadius: 4, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.04)", border: "1px solid #eef2f7" }}>
            <Stack direction={{ xs: "column", lg: "row" }} gap={2} alignItems={{ xs: "stretch", lg: "center" }}>
              <TextField
                label="Date From"
                type="date"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 170 }}
              />
              <TextField
                label="Date To"
                type="date"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 170 }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select value={status} label="Status" onChange={(event) => setStatus(event.target.value)}>
                  {STATUSES.map((entry) => (
                    <MenuItem key={entry || "all"} value={entry}>
                      {entry || "All"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Employee Code / Name"
                value={employeeSearch}
                onChange={(event) => setEmployeeSearch(event.target.value)}
                placeholder="e.g. EMP001 or Aung Aung"
                size="small"
                sx={{ minWidth: { xs: "100%", lg: 320 }, flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#64748b" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Paper>

          <Paper sx={{ flex: 1, p: 2, borderRadius: 4, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.04)", border: "1px solid #eef2f7", minHeight: 560 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 800 }}>
                {loading ? "Loading requests..." : `${rows.length.toLocaleString()} request${rows.length === 1 ? "" : "s"}`}
              </Typography>
              {loading && <CircularProgress size={20} sx={{ color: "#6366f1" }} />}
            </Stack>

            <Box
              className="ag-theme-quartz"
              sx={{
                height: "calc(100vh - 315px)",
                minHeight: 470,
                width: "100%",
                "& .premium-grid-header": { fontWeight: 800, color: "#475569" },
                "& .ag-header": { bgcolor: "#f8fafc" },
              }}
            >
              <AgGridReact
                rowData={rows}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                animateRows
                rowHeight={52}
                headerHeight={48}
                pagination
                paginationPageSize={25}
                overlayNoRowsTemplate="<span style='color:#64748b'>No manual requests found</span>"
              />
            </Box>
          </Paper>
        </Box>
      </Fade>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
